import { useCallback, useMemo } from 'react';
import useAuth from './useAuth';
import { getPermissionsForRole } from 'utils/permissions';
import type { AppPermission } from 'utils/permissions';

export interface UsePermissionsReturn {
  can: (permission: AppPermission) => boolean;
  canAny: (permissions: AppPermission[]) => boolean;
  canAll: (permissions: AppPermission[]) => boolean;
  hasRole: (role: string | string[]) => boolean;
  isReadOnly: boolean;
  userRole: string | string[] | undefined;
  permissions: AppPermission[];
}

export function usePermissions(): UsePermissionsReturn {
  const { user } = useAuth();

  const permissions = useMemo(() => getPermissionsForRole(user?.role), [user?.role]);

  const can = useCallback(
    (permission: AppPermission): boolean => permissions.includes(permission),
    [permissions]
  );

  const canAny = useCallback(
    (perms: AppPermission[]): boolean => perms.some((p) => permissions.includes(p)),
    [permissions]
  );

  const canAll = useCallback(
    (perms: AppPermission[]): boolean => perms.every((p) => permissions.includes(p)),
    [permissions]
  );

  const hasRole = useCallback(
    (role: string | string[]): boolean => {
      if (!user?.role) return false;
      const userRoles = Array.isArray(user.role) ? user.role : [user.role];
      const checkRoles = Array.isArray(role) ? role : [role];
      return checkRoles.some((r) => userRoles.includes(r));
    },
    [user?.role]
  );

  const isReadOnly = useMemo(() => {
    if (!user?.role) return false;
    const userRoles = Array.isArray(user.role) ? user.role : [user.role];
    return userRoles.includes('Supervisor') && userRoles.length === 1;
  }, [user?.role]);

  return {
    can,
    canAny,
    canAll,
    hasRole,
    isReadOnly,
    userRole: user?.role,
    permissions,
  };
}
